import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import i18next from 'i18next';
import { API_AUDIT_LOGS_QUERY } from './gql';

type ApiAuditLog = {
  id: number;
  createdAt: string;
  apiType: 'GRAPHQL' | 'REST';
  serviceName: string;
  userId?: number | null;
  userName?: string | null;
  userEmail?: string | null;
  clientId?: number | null;
  clientEmail?: string | null;
  sudoUserId?: number | null;
  sudoUserName?: string | null;
  sudoUserEmail?: string | null;
  operationName?: string | null;
  mutationNames?: string | null;
  httpMethod?: string | null;
  route?: string | null;
  handlerName?: string | null;
  endpoint?: string | null;
  requestId?: string | null;
  ipAddress?: string | null;
  responseStatus?: number | null;
  success: boolean;
  errorMessage?: string | null;
  jwtClaims?: string | null;
  tokenFingerprint?: string | null;
  releaseVersion?: string | null;
};

const PAGE_SIZE = 50;

interface Filters {
  apiType: string;
  serviceName: string;
  userSearch: string;
  operationName: string;
  success: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = {
  apiType: '',
  serviceName: '',
  userSearch: '',
  operationName: '',
  success: '',
  from: '',
  to: '',
};

function whoLabel(entry: ApiAuditLog): string {
  const subject = entry.userName || entry.userEmail || entry.clientEmail || (entry.userId ? `#${entry.userId}` : entry.clientId ? `client#${entry.clientId}` : '—');
  if (entry.sudoUserId) {
    return `${entry.sudoUserName || entry.sudoUserEmail || `#${entry.sudoUserId}`} → ${subject}`;
  }
  return subject;
}

function whatLabel(entry: ApiAuditLog): string {
  if (entry.apiType === 'GRAPHQL') {
    return entry.mutationNames || entry.operationName || '—';
  }
  return [entry.httpMethod, entry.route || entry.handlerName || entry.endpoint].filter(Boolean).join(' ') || '—';
}

export default function ApiAuditLogSection() {
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<ApiAuditLog | null>(null);

  const variables = useMemo(
    () => ({
      apiType: applied.apiType || null,
      serviceName: applied.serviceName || null,
      userSearch: applied.userSearch.trim() || null,
      operationName: applied.operationName.trim() || null,
      success: applied.success === '' ? null : applied.success === 'true',
      from: applied.from ? new Date(`${applied.from}T00:00:00`).toISOString() : null,
      to: applied.to ? new Date(`${applied.to}T23:59:59.999`).toISOString() : null,
      skip,
      take: PAGE_SIZE,
    }),
    [applied, skip],
  );

  const { data, loading, refetch } = useQuery(API_AUDIT_LOGS_QUERY, {
    variables,
    fetchPolicy: 'network-only',
  });

  const items: ApiAuditLog[] = data?.apiAuditLogs?.items || [];
  const total: number = data?.apiAuditLogs?.total || 0;
  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applyDraft = (next: Filters) => {
    setSkip(0);
    setDraft(next);
    setApplied(next);
  };

  const applyFilters = () => {
    setSkip(0);
    setApplied(draft);
  };

  return (
    <>
      <Card>
        <CardHeader
          title={String(i18next.t('system-audit-title'))}
          action={
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={loading}>
              {i18next.t('refresh')}
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{i18next.t('system-audit-filter-api-type')}</InputLabel>
                <Select
                  label={i18next.t('system-audit-filter-api-type')}
                  value={draft.apiType}
                  onChange={e => applyDraft({ ...draft, apiType: e.target.value })}
                >
                  <MenuItem value="">{i18next.t('system-audit-all')}</MenuItem>
                  <MenuItem value="GRAPHQL">GRAPHQL</MenuItem>
                  <MenuItem value="REST">REST</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{i18next.t('system-audit-filter-service')}</InputLabel>
                <Select
                  label={i18next.t('system-audit-filter-service')}
                  value={draft.serviceName}
                  onChange={e => applyDraft({ ...draft, serviceName: e.target.value })}
                >
                  <MenuItem value="">{i18next.t('system-audit-all')}</MenuItem>
                  <MenuItem value="seminargo-server">seminargo-server</MenuItem>
                  <MenuItem value="seminargo-service-pricelist">seminargo-service-pricelist</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{i18next.t('system-audit-filter-status')}</InputLabel>
                <Select
                  label={i18next.t('system-audit-filter-status')}
                  value={draft.success}
                  onChange={e => applyDraft({ ...draft, success: e.target.value })}
                >
                  <MenuItem value="">{i18next.t('system-audit-all')}</MenuItem>
                  <MenuItem value="true">{i18next.t('system-audit-success')}</MenuItem>
                  <MenuItem value="false">{i18next.t('system-audit-failure')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label={i18next.t('system-audit-filter-user')}
                value={draft.userSearch}
                onChange={e => setDraft({ ...draft, userSearch: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label={i18next.t('system-audit-filter-operation')}
                value={draft.operationName}
                onChange={e => setDraft({ ...draft, operationName: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={i18next.t('system-audit-filter-from')}
                InputLabelProps={{ shrink: true }}
                value={draft.from}
                onChange={e => setDraft({ ...draft, from: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={i18next.t('system-audit-filter-to')}
                InputLabelProps={{ shrink: true }}
                value={draft.to}
                onChange={e => setDraft({ ...draft, to: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="contained" onClick={applyFilters} disabled={loading} fullWidth>
                {i18next.t('system-audit-filter-apply')}
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18next.t('system-audit-col-time')}</TableCell>
                  <TableCell>{i18next.t('system-audit-col-type')}</TableCell>
                  <TableCell>{i18next.t('system-audit-col-service')}</TableCell>
                  <TableCell>{i18next.t('system-audit-col-who')}</TableCell>
                  <TableCell>{i18next.t('system-audit-col-what')}</TableCell>
                  <TableCell>{i18next.t('system-audit-col-status')}</TableCell>
                  <TableCell>{i18next.t('system-audit-col-version')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(entry => (
                  <TableRow key={entry.id} hover>
                    <TableCell>{new Date(entry.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip size="small" label={entry.apiType} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          entry.serviceName === 'seminargo-service-pricelist'
                            ? 'pricelist'
                            : entry.serviceName === 'seminargo-server'
                              ? 'server'
                              : entry.serviceName
                        }
                      />
                    </TableCell>
                    <TableCell>{whoLabel(entry)}</TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {whatLabel(entry)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={entry.success ? 'success' : 'error'}
                        label={entry.success ? i18next.t('system-audit-success') : i18next.t('system-audit-failure')}
                      />
                    </TableCell>
                    <TableCell>{entry.releaseVersion || '—'}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setSelected(entry)}>
                        {i18next.t('system-audit-details')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        {i18next.t('system-audit-empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {i18next.t('system-audit-total', { total })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" disabled={skip <= 0 || loading} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                {i18next.t('system-audit-previous')}
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {page} / {pageCount}
              </Typography>
              <Button size="small" disabled={skip + PAGE_SIZE >= total || loading} onClick={() => setSkip(skip + PAGE_SIZE)}>
                {i18next.t('system-audit-next')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{i18next.t('system-audit-details-title')}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={2}>
              {(
                [
                  ['id', selected.id],
                  ['createdAt', new Date(selected.createdAt).toLocaleString()],
                  ['apiType', selected.apiType],
                  ['serviceName', selected.serviceName],
                  ['who', whoLabel(selected)],
                  ['userId', selected.userId],
                  ['userEmail', selected.userEmail],
                  ['clientId', selected.clientId],
                  ['clientEmail', selected.clientEmail],
                  ['sudoUserId', selected.sudoUserId],
                  ['sudoUserName', selected.sudoUserName],
                  ['sudoUserEmail', selected.sudoUserEmail],
                  ['operationName', selected.operationName],
                  ['mutationNames', selected.mutationNames],
                  ['httpMethod', selected.httpMethod],
                  ['route', selected.route],
                  ['handlerName', selected.handlerName],
                  ['endpoint', selected.endpoint],
                  ['requestId', selected.requestId],
                  ['ipAddress', selected.ipAddress],
                  ['responseStatus', selected.responseStatus],
                  ['success', String(selected.success)],
                  ['errorMessage', selected.errorMessage],
                  ['tokenFingerprint', selected.tokenFingerprint],
                  ['releaseVersion', selected.releaseVersion],
                ] as [string, unknown][]
              ).map(([key, value]) =>
                value == null || value === '' ? null : (
                  <Grid item xs={12} sm={6} key={key}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {key}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {String(value)}
                    </Typography>
                  </Grid>
                ),
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  jwtClaims
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: 12,
                  }}
                >
                  {selected.jwtClaims
                    ? (() => {
                        try {
                          return JSON.stringify(JSON.parse(selected.jwtClaims), null, 2);
                        } catch {
                          return selected.jwtClaims;
                        }
                      })()
                    : '—'}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>{i18next.t('close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
